'use client'
import * as React from 'react'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { TeamMember } from './team-member'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import { useMyMembershipInTeam } from '~/hooks-data/use-my-membership'
import { SidebarSection } from '../components/section'
import Link from 'next/link'
import { useTeamCreateFormContext } from '~/app/(dash)/[team-slug]/header/team-dropdown-menu/dialog/context'
import { ArrowRightIcon, ChevronDownIcon } from '@radix-ui/react-icons'
import { ExpandButton } from '../components/expand-button'
import { clsx } from 'clsx'
import {
  PublicTeam,
  isPublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { Team } from '@basehub/data/replicache/index'
import { CoworkersIcon } from '~/components/icons/coworkers'

const INITIAL_MEMBERS = 5

export const TeamMembers = ({
  staticTeamData,
}: {
  staticTeamData?: PublicTeam | Team
}) => {
  const { team } = useSelectedTeam(staticTeamData)
  const myMembership = useMyMembershipInTeam(null)
  const { setStep } = useTeamCreateFormContext()

  const [pagination, setPagination] = React.useState({
    page: 1,
    perPage: INITIAL_MEMBERS,
  })

  const members = team?.members

  // do we want to fetch and sort all bookmarks here?
  const paginatedData = React.useMemo(() => {
    if (!members) return []
    return members
      .sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
      .slice(0, pagination.page * pagination.perPage)
  }, [members, pagination])

  const handleNextPage = () => {
    if (members && members.length <= pagination.page * pagination.perPage) {
      return
    }
    setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
  }

  const isPersonal =
    team &&
    (('plan' in team && team?.plan === 'personal') ||
      ('plan_v2' in team && team?.plan_v2?.type === 'personal'))

  if (!team || (!myMembership?.role && isPersonal)) return null
  return (
    <SidebarSection
      title="People"
      headingChildren={
        myMembership?.role === 'owner' &&
        !isPersonal && (
          <ButtonLink
            unstyled
            href={`/${team.slug}/settings/members`}
            className="text-dark-control-base hover:text-dark-control-highlight cursor-default ml-2"
          >
            Add
          </ButtonLink>
        )
      }
    >
      <ul className="flex flex-col gap-2">
        {paginatedData.map((member) => (
          <TeamMember
            userId={member.userId}
            key={member.userId}
            linkToPersonalTeam={isPublicTeam(team)}
          />
        ))}
        {team?.members.length > INITIAL_MEMBERS &&
          (myMembership?.role ? (
            <ExpandButton
              icon={<ArrowRightIcon />}
              href={`/${team.slug}/settings/members`}
              className="py-1 border-b inline-flex w-min whitespace-nowrap items-center flex-row-reverse border-dark-muted gap-1 text-xs text-dark-muted hover:text-dark-bright focus-visible:outline focus-visible:outline-dark-faint focus-visible:outline-offset-2 focus-visible:outline-offset-dark-faint cursor-default"
            >
              See {team?.members.length - INITIAL_MEMBERS} more
            </ExpandButton>
          ) : (
            team?.members.length > INITIAL_MEMBERS && (
              <ExpandButton
                onClick={handleNextPage}
                icon={<ChevronDownIcon />}
                className={clsx({
                  hidden: team?.members.length <= paginatedData.length,
                })}
              >
                Load more
              </ExpandButton>
            )
          ))}
      </ul>
      {myMembership?.role === 'owner' && isPersonal && (
        <Link
          className="text-xs p-2 w-max mt-1 bg-dark-base border-dark-border-solid hover:border-dark-control-base border rounded-md transition-colors flex gap-2 items-center"
          href="?create-team=true"
          onClick={() => setStep('form')}
        >
          <CoworkersIcon />
          <span>Bring your coworkers together with a Team</span>
        </Link>
      )}
    </SidebarSection>
  )
}
